import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	Output,
	ViewChild
}                               from '@angular/core';
import { BaseComponent }        from '@core/bases/base-component/base.component';
import {
	NgbDate,
	NgbDateAdapter,
	NgbDateNativeUTCAdapter,
	NgbDatepickerConfig,
	NgbDatepickerI18n,
	NgbDateStruct,
	NgbInputDatepicker
}                               from '@ng-bootstrap/ng-bootstrap';
import { CalendarModeEnum }     from '@shared/components/date-picker/date-picker.model';
import { MonthPickerComponent } from '@shared/components/month-picker/month-picker.component';
import {
	DateRangeFilterType,
	FilterDatepickerType,
	FilterTypes,
	IFilter,
	IFilterProperties
}                               from '@shared/list-operations/model/filter.model';
import {
	IResetEvent,
	ListOperationsResetModeEnum
}                               from '@shared/list-operations/model/list-operations.model';
import { CustomDatepickerI18n } from '@shared/services/ngb-datepicker-custom-services/custom-datepicker-i18n.service';
import { DateUtils }            from '@utils/helpers';
import { Observable }           from 'rxjs';
import { takeUntil }            from 'rxjs/operators';

@Component( {
	selector: 'app-datepicker-filter[defaultFilterData]',
	templateUrl: './datepicker-filter.component.html',
	styleUrls: [ './datepicker-filter.component.scss' ],
	providers: [
		NgbDatepickerConfig,
		{ provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
		{ provide: NgbDateAdapter, useClass: NgbDateNativeUTCAdapter }
	]
} )
export class DatepickerFilterComponent extends BaseComponent implements AfterViewInit {

	private _blockEmit: boolean;

	private _dateValue: FilterDatepickerType;

	private _minDate: Date;

	private _maxDate: Date;

	@Input() dateRangeType: DateRangeFilterType;

	@Input() id: string;

	@Input() defaultFilterData: IFilter;

	@Input() shouldReset: Observable<IResetEvent>;

	@Input() placeholder: string;

	@Input() readOnly: boolean;

	get minDate(): Date {
		return this._minDate;
	}

	@Input() set minDate( minDate: Date ) {
		if ( minDate ) {
			switch ( this.calendarMode ) {
				case CalendarModeEnum.DAY:
					this.minDateDayCalendarMode = this.setDateLimitNgbDate( minDate, undefined );
					break;
				case CalendarModeEnum.MONTH:
					this._minDate = minDate;
					break;
			}
		}
	}

	get maxDate(): Date {
		return this._maxDate;
	}

	@Input() set maxDate( maxDate: Date ) {
		if ( maxDate ) {
			switch ( this.calendarMode ) {
				case CalendarModeEnum.DAY:
					this.maxDateDayCalendarMode = this.setDateLimitNgbDate( maxDate, undefined );
					break;
				case CalendarModeEnum.MONTH:
					this._maxDate = maxDate;
					break;
			}
		}
	}

	@Output() change: EventEmitter<FilterDatepickerType> = new EventEmitter();

	@ViewChild( NgbInputDatepicker ) ngDatepickerComponent: NgbInputDatepicker;

	@ViewChild( 'monthPicker', { static: true } ) monthPickerComponent: MonthPickerComponent;

	readonly calendarModeEnum = CalendarModeEnum;

	calendarMode: CalendarModeEnum = CalendarModeEnum.DAY;

	dateInputFormat: Intl.DateTimeFormatOptions;

	dayPickerControlValue: Date;

	monthPickerControlValue: Date;

	minDateDayCalendarMode: NgbDateStruct;

	maxDateDayCalendarMode: NgbDateStruct;

	constructor(
		private readonly changeDetectorRef: ChangeDetectorRef
	) {
		super();
	}

	private processFilterProperties( properties: IFilterProperties ): void {
		if ( properties ) {
			this.calendarMode = properties.calendarMode ?? this.calendarMode;
			this.dateInputFormat = properties.dateInputFormat;

			switch ( this.calendarMode ) {
				case CalendarModeEnum.DAY:
					this.minDateDayCalendarMode = this.setDateLimitNgbDate( this.minDate, properties.minDate );
					this.maxDateDayCalendarMode = this.setDateLimitNgbDate( this.maxDate, properties.maxDate );
					break;
				case CalendarModeEnum.MONTH:
					this.minDate = new Date( properties.minDate );
					this.maxDate = new Date( properties.maxDate );
					break;
			}
		}
	}

	private setDateLimitNgbDate( inputDate: Date, limitDate: string ): NgbDateStruct {
		if ( inputDate === undefined && limitDate !== undefined ) {
			const date: Date = new Date( limitDate );
			return DateUtils.dateToNgbDateStruct( date );
		} else if ( inputDate !== undefined ) {
			return DateUtils.dateToNgbDateStruct( inputDate );
		}
		return undefined;
	}

	private setDefaultValue(): void {
		let dateValue: string = null;
		switch ( this.defaultFilterData?.type ) {
			case FilterTypes.FILTER_DATE_RANGE_PICKER_TYPE:

				let dateRange: string[] = [];
				if ( this.defaultFilterData?.values?.length === 1 ) {
					dateRange = this.defaultFilterData?.values[ 0 ] as string[];
				}

				switch ( this.dateRangeType ) {
					case DateRangeFilterType.FROM:
						if ( dateRange?.length > 0 ) {
							dateValue = dateRange[ 0 ] ?? null;
						}
						break;

					case DateRangeFilterType.TO:
						if ( dateRange?.length > 1 ) {
							dateValue = dateRange[ 1 ] ?? null;
						}
						break;
				}
				break;

			case FilterTypes.FILTER_DATE_PICKER_TYPE:
				if ( this.defaultFilterData?.values?.length === 1 ) {
					dateValue = this.defaultFilterData?.values[ 0 ] as string;
				}
				break;
		}

		if ( dateValue ) {
			const defaultDate: Date = new Date( dateValue );

			switch ( this.calendarMode ) {
				case CalendarModeEnum.DAY:

					const ngbDateStruct: NgbDateStruct = DateUtils.dateToNgbDateStruct( defaultDate );

					const timeout = setTimeout( () => {
						clearTimeout( timeout );
						this.dayPickerControlValue = defaultDate;
						this.ngDatepickerComponent.startDate = ngbDateStruct;
					}, 0 );
					break;

				case CalendarModeEnum.MONTH:
					this.monthPickerControlValue = defaultDate;
					break;

			}
		}
	}

	private emitFilterItemChange(): void {
		if ( !this._blockEmit ) {
			this.change.emit( this._dateValue );
		}
	}

	ngAfterViewInit(): void {
		if ( this.defaultFilterData ) {
			// TODO will be replaced by store resetState selector
			this.shouldReset
			.pipe( takeUntil( this.ngUnsubscribe ) )
			.subscribe( ( resetEvent: IResetEvent ) => {

				// TODO service property will be obsolete and replaced by array for individual filterIdentifiers of a (currently not finally specified)
				//  IListOperationsReset interface
				if ( resetEvent && resetEvent.service === this.defaultFilterData.service ) {

					this._blockEmit = resetEvent.blockEmit;

					switch ( resetEvent.resetMode ) {

						case ListOperationsResetModeEnum.CLEAR:

							switch ( this.calendarMode ) {
								case CalendarModeEnum.DAY:
									this.ngDatepickerComponent.writeValue( '' );
									break;
								case CalendarModeEnum.MONTH:
									this.monthPickerControlValue = null;
									break;
							}
							this.minDate = null;
							break;

						case ListOperationsResetModeEnum.RESTORE:
							break;
					}
				}
			} );

			this.processFilterProperties( this.defaultFilterData.properties );
			this.setDefaultValue();
			this.changeDetectorRef.detectChanges();
		}
	}

	setDate( datePicker: NgbInputDatepicker ) {
		datePicker.toggle();
	}

	setSelectedDate( date: Date ) {
		this.monthPickerControlValue = date;
		this._dateValue = date;
		this.emitFilterItemChange();
	}

	onChange( ngbDate: NgbDate ): void {
		const date: Date = new Date( ngbDate.year, ngbDate.month - 1, ngbDate.day );
		this.dayPickerControlValue = date;
		this._dateValue = date;
		this.emitFilterItemChange();
	}

	toggleMonthPicker(): void {
		this.monthPickerComponent.toggle();
	}
}
